import React, {
  forwardRef, useCallback, useEffect, useMemo, useState,
} from 'react';

import { ControlledMap } from '@vlabs/monokit/shared/ui/controlled-map';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import validate from '@vlabs/shared/validators';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import { Input } from '@vlabs/uikit/components/controls/input';
import debounce from 'lodash/debounce';

import { useWorkspaceTranslation } from '../../../i18n';
import { useWorkspaceFilters } from '../../useWorkspaceFilters';
import { WORKSPACE_LOCATION_FILTER_FIELDS } from '../../workspaceFiltersModel';
import type { VideoAnalyticType } from '../../workspaceFiltersModel';
import st from '../FilterPopovers.module.sass';

const ADDRESS_MAX_LENGTH = 36;
const locationFields = WORKSPACE_LOCATION_FILTER_FIELDS;

type LocationField = typeof locationFields[number];
type LocationValues = Record<LocationField, unknown>;
type LocationErrors = Partial<Record<LocationField, string>>;
type GeolocationTab = 'map' | 'address' | 'coordinates';

interface GeolocationFilterPopoverProps extends PopoverWithButtonProps {
  analyticType?: VideoAnalyticType;
  idPrefix?: string;
}

function getLocationValues(source: Record<string, unknown>): LocationValues {
  return locationFields.reduce<LocationValues>((result, field) => ({
    ...result,
    [field]: source[field],
  }), {} as LocationValues);
}

function isFilled(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function getNumberError(value: unknown, min: number, max: number): string | undefined {
  const stringValue = String(value ?? '');
  const numberValidation = validate.number()(stringValue);
  if (numberValidation !== true) return String(numberValidation);
  if (stringValue === '') return undefined;

  const numberValue = Number(stringValue);
  if (numberValue < min) return String(validate.gte(min).message);
  if (numberValue > max) return String(validate.lte(max).message);
  return undefined;
}

function getLocationErrors(values: LocationValues): LocationErrors {
  const errors = locationFields.reduce<LocationErrors>((result, field) => {
    const value = String(values[field] ?? '');
    if (['cities', 'areas', 'districts', 'streets', 'house_numbers'].includes(field)
      && value.length > ADDRESS_MAX_LENGTH) {
      return { ...result, [field]: String(validate.maxLength(ADDRESS_MAX_LENGTH).message) };
    }
    return result;
  }, {});

  errors.origin_longitude = getNumberError(values.origin_longitude, -180, 180);
  errors.origin_latitude = getNumberError(values.origin_latitude, -90, 90);
  errors.longitude_delta = getNumberError(values.longitude_delta, 0, 90);
  errors.latitude_delta = getNumberError(values.latitude_delta, 0, 90);

  if (!isFilled(values.origin_longitude) && (isFilled(values.origin_latitude) || isFilled(values.longitude_delta))) {
    errors.origin_longitude = String(validate.required());
  }
  if (!isFilled(values.origin_latitude) && (isFilled(values.origin_longitude) || isFilled(values.latitude_delta))) {
    errors.origin_latitude = String(validate.required());
  }

  return Object.fromEntries(
    Object.entries(errors).filter(([, error]) => error !== undefined),
  );
}

function GeolocationFilterPopover(
  {
    analyticType = undefined,
    idPrefix = 'workspace',
    ...props
  }: GeolocationFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useWorkspaceTranslation();
  const { filters, updateFilters, updateVideoAnalyticsFilters } = useWorkspaceFilters();
  const source = analyticType
    ? filters.videoAnalytics[analyticType].filters
    : filters;
  const storedValues = useMemo(() => getLocationValues(source), [source]);
  const [values, setValues] = useState<LocationValues>(storedValues);
  const [activeTab, setActiveTab] = useState<GeolocationTab>('map');
  const [mapResetTick, setMapResetTick] = useState(0);
  const activeCount = locationFields.some((field) => isFilled(values[field])) ? 1 : 0;
  const errors = getLocationErrors(values);

  useEffect(() => setValues(storedValues), [storedValues]);
  const applyValues = useCallback((nextValues: LocationValues) => {
    if (!analyticType) {
      updateFilters(nextValues);
      return;
    }
    updateVideoAnalyticsFilters(analyticType, nextValues);
  }, [analyticType, updateFilters, updateVideoAnalyticsFilters]);

  const applyValuesDebounced = useMemo(
    () => debounce(applyValues, 500),
    [applyValues],
  );
  useEffect(() => () => applyValuesDebounced.cancel(), [applyValuesDebounced]);

  const changeField = (field: LocationField, value: unknown) => {
    const nextValues = { ...values, [field]: value === '' ? undefined : value };
    setValues(nextValues);
    applyValuesDebounced.cancel();
    if (Object.keys(getLocationErrors(nextValues)).length > 0) return;
    applyValuesDebounced(nextValues);
  };

  const changeCoordinates = (coordinates: Partial<LocationValues>) => {
    applyValuesDebounced.cancel();
    const nextValues = { ...values, ...coordinates };
    setValues(nextValues);
    applyValues(nextValues);
  };

  const reset = () => {
    applyValuesDebounced.cancel();
    const nextValues = getLocationValues({});
    setValues(nextValues);
    setMapResetTick((current) => current + 1);
    applyValues(nextValues);
  };

  const renderInput = (
    field: LocationField,
    label: string,
    className?: string,
  ) => (
    <div className={className}>
      <Input
        errorMessage={errors[field]}
        hasError={errors[field] !== undefined}
        id={`${idPrefix}.${field}`}
        isClearable
        label={label}
        onChangeValue={(value) => changeField(field, value)}
        onClear={() => changeField(field, '')}
        value={String(values[field] ?? '')}
      />
    </div>
  );

  return (
    <PopoverWithButton
      {...props}
      className={st.GeolocationPopover}
      isButtonDisabled={activeCount === 0}
      onClickReset={reset}
      ref={ref}
    >
      <div className={st.SwitchWrapper}>
        <ButtonSwitchGroup
          kind="secondary"
          onChange={setActiveTab}
          size="small"
          testId="geolocationFiltersSwitch"
          value={activeTab}
          variant="square"
        >
          <ButtonSwitch
            value="map"
            variant="invisible"
          >
            {t('geolocation_filter.map')}
          </ButtonSwitch>
          <ButtonSwitch
            value="address"
            variant="invisible"
          >
            {t('geolocation_filter.address')}
          </ButtonSwitch>
          <ButtonSwitch
            value="coordinates"
            variant="invisible"
          >
            {t('geolocation_filter.coordinates')}
          </ButtonSwitch>
        </ButtonSwitchGroup>
      </div>

      {activeTab === 'map' && (
        <ControlledMap
          id={`${idPrefix}.map`}
          key={mapResetTick}
          onChange={changeCoordinates}
          value={values}
        />
      )}

      {activeTab === 'address' && (
        <React.Fragment>
          {renderInput('cities', t('geolocation_filter.city'))}
          {renderInput('areas', t('geolocation_filter.region'))}
          {renderInput('districts', t('geolocation_filter.district'))}
          <div className={st.AddressRow}>
            {renderInput('streets', t('geolocation_filter.street'), st.AddressStreet)}
            {renderInput('house_numbers', t('geolocation_filter.house_number'), st.AddressHouse)}
          </div>
        </React.Fragment>
      )}

      {activeTab === 'coordinates' && (
        <React.Fragment>
          <div className={st.CoordinatesRow}>
            {renderInput('origin_longitude', t('geolocation_filter.longitude'), st.CoordinateInput)}
            {renderInput('longitude_delta', t('geolocation_filter.accuracy'), st.CoordinateInput)}
          </div>
          <div className={st.CoordinatesRow}>
            {renderInput('origin_latitude', t('geolocation_filter.latitude'), st.CoordinateInput)}
            {renderInput('latitude_delta', t('geolocation_filter.accuracy'), st.CoordinateInput)}
          </div>
        </React.Fragment>
      )}
    </PopoverWithButton>
  );
}

const GeolocationFilterPopoverWithRef = forwardRef(GeolocationFilterPopover);
export { GeolocationFilterPopoverWithRef as GeolocationFilterPopover };
